import { parseSMSMessage } from '@/components/sms/parse-sms-message';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { dealStore } from '@/models/stores';
import { confirmationsStore } from '@/models/stores/confirmations';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types';
import { CircleSmall } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Badge } from './badge';
import {
  PDKConfirmationByPaddockCheckedIcon,
  PDKConfirmationCheckedIcon,
} from './pdk-icons';

type DealUserEngagedBadgeProps = {
  dealId: string;
  className?: string;
  size?: 'default' | 'compact';
};

const CONFIRMATION_FIELDS_TO_CHECK = [
  ConfirmationField.CustomerName,
  ConfirmationField.CustomerEmail,
  ConfirmationField.CustomerPhoneNumber,
  ConfirmationField.CustomerRegistrationAddress,
  ConfirmationField.CustomerDeliveryAddress,
];

export const DealUserEngagedBadge = observer(
  ({ dealId, className, size = 'default' }: DealUserEngagedBadgeProps) => {
    const deal = dealStore.getById(dealId);
    const agentConversation = deal?.sms_agent_messages;
    const smsConversation = deal?.sms_conversation;

    // Check if the customer has responded to the agent
    const hasCustomerResponseToAgent = agentConversation?.some(
      (message) => message.direction === 'inbound',
    );

    // Check if the customer has replied in the SMS conversation
    const hasCustomerSMSReply = smsConversation?.some((message) => {
      const parsed = parseSMSMessage(message);
      return parsed.isCustomer;
    });

    // Check if any customer info is confirmed and track who confirmed it
    let hasConfirmedInfo = false;
    let hasPaddockConfirmation = false;

    CONFIRMATION_FIELDS_TO_CHECK.forEach((field) => {
      const confirmation = confirmationsStore.getByTargetAndField(
        dealId,
        ConfirmationTargetType.Deal,
        field,
      );

      if (confirmation?.confirmed === true) {
        hasConfirmedInfo = true;

        // Check if the most recent confirmation was by paddock user
        const mostRecentEntry = confirmation.most_recent_entry;
        if (mostRecentEntry?.confirmed_by?.email === 'paddock@evercars.com') {
          hasPaddockConfirmation = true;
        }
      }
    });

    // Show badge if there's either agent engagement or SMS replies from customer
    const hasCustomerEngagement =
      hasCustomerResponseToAgent || hasCustomerSMSReply;

    if (!hasCustomerEngagement) {
      return null;
    }

    // Only show if the deal is in new status
    if (deal?.status !== 'new') {
      return null;
    }

    const isCompact = size === 'compact';

    return (
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <div>
            <Badge
              className={cn(
                'flex flex-row items-center gap-1 text-black bg-white  text-xs shadow-none rounded-full border-foreground/10 pointer-events-none pl-1 pr-2',
                className,
              )}
            >
              <CircleSmall
                fill="var(--color-green-500)"
                stroke="var(--color-green-500)"
                className="size-4"
              />
              <span className={cn(isCompact && 'truncate')}>
                {hasCustomerSMSReply ? 'Engaged w/ Team' : 'Engaged w/ Bot'}
              </span>
              {hasConfirmedInfo &&
                (hasPaddockConfirmation ? (
                  <PDKConfirmationByPaddockCheckedIcon />
                ) : (
                  <PDKConfirmationCheckedIcon />
                ))}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-1 text-xs">
            {hasCustomerSMSReply ? (
              <>
                Customer has engaged via SMS.
                <br />
                Please reach out to the customer soon!
                <br />
                Review `Text Convo` to see all conversations.
              </>
            ) : hasCustomerResponseToAgent ? (
              <>
                Customer responded to the AI agent and is engaged.
                <br />
                Please reach out to the customer soon!
                <br />
                Review the `Agent Convo` on the Deal Page to see the
                conversation.
              </>
            ) : (
              <>
                Customer has replied via SMS and is engaged.
                <br />
                Please reach out to the customer soon!
                <br />
                Review the `Text Convo` on the Deal Page to see the
                conversation.
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  },
);
